import React, { useContext, useState, useMemo } from 'react';
import { Autocomplete, TextField, IconButton, Box, Button, Typography, TableRow, TableCell } from '@mui/material';
import { UserPlus } from '@phosphor-icons/react';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { ThemeContext } from 'styled-components';
import { IsValidEmail } from '../../../utils/Validators';
import { getGroupParticipantsExcludingBill, getGroupParticipants } from '../../../services/group/Groups';
import { useQuery } from 'react-query';
import { CsrfContext } from '../../../providers/CsrfProvider';
import { AuthContext } from '../../auth/providers/AuthProvider';
import { TemplateContext } from '../../../providers/TemplateProvider';

const AddParticipantRow = ({ 
    templateId, 
    groupId, 
    participants: participantsProp,
    onSubmit, 
    isOwner,
    isAddingUser,
    setIsAddingUser,
    editingUserId,
    setEditingUserId
}) => {
    const theme = useContext(ThemeContext);
    const { csrfToken } = useContext(CsrfContext);
    const { authClaims } = useContext(AuthContext);
    const { ownerSub } = useContext(TemplateContext);
    const [email, setEmail] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [dropdownEverOpened, setDropdownEverOpened] = useState(false);
    const [autocompleteOpen, setAutocompleteOpen] = useState(false);

    // Fetch group members (excluding current bill participants) when user opens the add-participant dropdown
    const { data: groupMembers, isLoading: loadingGroupMembers } = useQuery(
        ['groupParticipantsExcludingBill', groupId, templateId],
        () => getGroupParticipantsExcludingBill(groupId, templateId, csrfToken),
        {
            enabled: !!groupId && !!templateId && !!csrfToken && templateId !== 'none' && dropdownEverOpened,
            staleTime: 0,
            refetchOnMount: 'always',
            refetchOnWindowFocus: false,
        }
    );

    // Fetch all group participants only when add-participant dropdown is opened (not on template view load)
    const { data: allGroupParticipants } = useQuery(
        ['groupParticipants', groupId],
        () => getGroupParticipants(groupId, csrfToken),
        {
            enabled: !!groupId && !!csrfToken && dropdownEverOpened,
            staleTime: 0,
            refetchOnMount: 'always',
            refetchOnWindowFocus: false,
        }
    );

    // Use participants from parent when provided (template view: single fetch in ParticipantsModel); otherwise no need for currentParticipants in dropdown logic
    const currentParticipants = participantsProp ?? null;

    // Ensure current user is included in dropdown options if they're not already there
    const enhancedGroupMembers = useMemo(() => {
        if (loadingGroupMembers) return [];
        if (!groupMembers) return [];
        
        // Check if current user (by user_sub) is already in group members
        const currentUserInGroup = groupMembers.some(member => {
            const memberSub = typeof member === 'string' ? null : (member.user_sub || member.sub);
            return memberSub && authClaims?.user_sub && memberSub.toUpperCase() === authClaims.user_sub.toUpperCase();
        });

        // If current user is not in excluded group members, check if they're in all group participants
        if (!currentUserInGroup && authClaims?.user_sub && allGroupParticipants) {
            const currentUserInAllParticipants = allGroupParticipants.find(member => {
                const memberSub = typeof member === 'string' ? null : (member.user_sub || member.sub);
                return memberSub && memberSub.toUpperCase() === authClaims.user_sub.toUpperCase();
            });

            if (currentUserInAllParticipants) {
                const currentUserEmail = typeof currentUserInAllParticipants === 'string' 
                    ? currentUserInAllParticipants 
                    : currentUserInAllParticipants.email;
                
                if (currentUserEmail) {
                    // Check if current user's email is already in group members (by email match)
                    const emailInGroup = groupMembers.some(member => {
                        const memberEmail = typeof member === 'string' ? member : member.email;
                        return memberEmail && memberEmail.toLowerCase() === currentUserEmail.toLowerCase();
                    });
                    
                    if (!emailInGroup) {
                        // Add current user to group members
                        return [...groupMembers, typeof currentUserInAllParticipants === 'string' 
                            ? currentUserInAllParticipants 
                            : { ...currentUserInAllParticipants, email: currentUserEmail }];
                    }
                }
            }
        }

        // Fallback: If current user is the owner and they're already a participant, try to get their email
        if (!currentUserInGroup && authClaims?.user_sub && ownerSub) {
            const isCurrentUserOwner = authClaims.user_sub.toUpperCase() === ownerSub.toUpperCase();
            if (isCurrentUserOwner && currentParticipants) {
                const ownerParticipant = currentParticipants.find(p => {
                    const pSub = p.user_sub || p.sub;
                    return pSub && pSub.toUpperCase() === ownerSub.toUpperCase();
                });
                
                if (ownerParticipant && ownerParticipant.email) {
                    // Check if owner's email is already in group members
                    const emailInGroup = groupMembers.some(member => {
                        const memberEmail = typeof member === 'string' ? member : member.email;
                        return memberEmail && ownerParticipant.email && 
                               memberEmail.toLowerCase() === ownerParticipant.email.toLowerCase();
                    });
                    
                    if (!emailInGroup) {
                        // Add current user to group members
                        return [...groupMembers, { email: ownerParticipant.email, user_sub: ownerSub }];
                    }
                }
            }
        }
        
        return groupMembers;
    }, [groupMembers, loadingGroupMembers, authClaims?.user_sub, ownerSub, currentParticipants, allGroupParticipants]);

    const handleStartAdd = () => {
        setEditingUserId(null); // Close any edit form if open
        setIsAddingUser(true);
        setEmail('');
    };

    const handleCancelAdd = () => {
        setIsAddingUser(false);
        setEmail('');
    };

    const handleAutocompleteChange = (event, value) => {
        // When user selects from dropdown (clicking an option)
        if (value && !isSubmitting) {
            const emailValue = typeof value === 'string' ? value : value.email;
            if (emailValue && emailValue.trim() !== '' && IsValidEmail(emailValue.trim())) {
                // Prevent double submission
                setIsSubmitting(true);
                // Auto-submit when selecting from dropdown
                onSubmit({ email: emailValue.trim() });
                setEmail('');
                setIsAddingUser(false);
                // Reset submitting state after a short delay
                setTimeout(() => setIsSubmitting(false), 1000);
            }
        }
    };

    const handleInputChange = (event, newInputValue, reason) => {
        // Update email when user types
        if (reason === 'input' || reason === 'clear') {
            setEmail(newInputValue);
        }
    };

    const handleSubmitForm = (e) => {
        if (e) {
            e.preventDefault();
        }
        const emailValue = email.trim();
        if (emailValue && IsValidEmail(emailValue) && !isSubmitting) {
            // Prevent double submission
            setIsSubmitting(true);
            onSubmit({ email: emailValue });
            setEmail('');
            setIsAddingUser(false);
            // Reset submitting state after a short delay
            setTimeout(() => setIsSubmitting(false), 1000);
        }
    };

    const isValidEmail = email.trim() !== '' && IsValidEmail(email.trim());

    return (
        <>
            {/* Header Row (when adding) */}
            {isAddingUser && (
                <TableRow>
                    <TableCell sx={{ width: '3.5rem', minWidth: '3.5rem', maxWidth: '3.5rem', p: 0, borderBottom: 'none' }}></TableCell>
                    <TableCell colSpan={2} sx={{ pl: 0, pr: { xs: '0.125rem', sm: 0 }, py: 1, borderBottom: 'none' }}>
                        <Typography variant="h6" sx={{ color: theme.colors.text, fontWeight: 600 }}>
                            Add Participant
                        </Typography>
                    </TableCell>
                    <TableCell sx={{ 
                        width: '3.5rem !important', 
                        minWidth: '3.5rem !important', 
                        maxWidth: '3.5rem !important', 
                        p: 0,
                        flexShrink: 0,
                        flexGrow: 0,
                        borderBottom: 'none',
                    }}>
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: '100%',
                            height: '100%'
                        }}>
                            <IconButton
                                onClick={handleCancelAdd}
                                size="small"
                                sx={{
                                    color: 'inherit',
                                    padding: '4px',
                                    '&:hover': {
                                        backgroundColor: theme.colors.overlayHover,
                                    },
                                }}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </TableCell>
                </TableRow>
            )}
            
            {/* Form Row (when adding) */}
            {isAddingUser && (
                <TableRow>
                    <TableCell sx={{ width: '3.5rem', minWidth: '3.5rem', maxWidth: '3.5rem', p: 0 }}></TableCell>
                    <TableCell colSpan={2} sx={{ pl: 0, pr: { xs: '0.125rem', sm: 0 }, py: 2 }}>
                        <Box
                            component="form"
                            onSubmit={handleSubmitForm}
                            sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                        >
                            <Autocomplete
                                freeSolo
                                open={autocompleteOpen}
                                onOpen={() => { setAutocompleteOpen(true); setDropdownEverOpened(true); }}
                                onClose={() => setAutocompleteOpen(false)}
                                options={groupId && !loadingGroupMembers ? (enhancedGroupMembers || []) : []}
                                getOptionLabel={(option) => {
                                    if (typeof option === 'string') {
                                        return option;
                                    }
                                    return option.email || '';
                                }}
                                inputValue={email}
                                onInputChange={handleInputChange}
                                onChange={handleAutocompleteChange}
                                filterOptions={(options, params) => {
                                    const filtered = options.filter((option) => {
                                        const emailValue = typeof option === 'string' ? option : option.email;
                                        return emailValue.toLowerCase().includes(params.inputValue.toLowerCase());
                                    });
                                    return filtered;
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={groupId && groupMembers && groupMembers.length > 0 ? "Search group members or type email" : "Enter email"}
                                        placeholder="Type to search or enter any email"
                                        onKeyDown={(e) => {
                                            // Handle Enter key to submit
                                            if (e.key === 'Enter' && !e.shiftKey) {
                                                e.preventDefault();
                                                const emailValue = email.trim();
                                                if (emailValue && IsValidEmail(emailValue) && !isSubmitting) {
                                                    handleSubmitForm(e);
                                                }
                                            }
                                            // Handle Escape to close
                                            if (e.key === 'Escape') {
                                                handleCancelAdd();
                                            }
                                        }}
                                        autoFocus
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                color: theme.colors.text,
                                                fontSize: '16px',
                                                '& fieldset': {
                                                    borderColor: theme.colors.secondary || theme.colors.brandSecondary,
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: theme.colors.text,
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: theme.colors.text,
                                                },
                                            },
                                            '& .MuiInputLabel-root': {
                                                color: theme.colors.secondary || theme.colors.brandSecondary,
                                                fontSize: '16px',
                                                '&.Mui-focused': {
                                                    color: theme.colors.text,
                                                },
                                            },
                                            '& .MuiInputBase-input': {
                                                fontSize: '16px',
                                            },
                                        }}
                                    />
                                )}
                                loading={loadingGroupMembers}
                            />
                            
                            {!isValidEmail ? (
                                <Button
                                    type="button"
                                    onClick={handleCancelAdd}
                                    variant="outlined"
                                    fullWidth
                                    sx={{
                                        border: `1px solid ${theme.colors.border || '#ccc'}`,
                                        backgroundColor: 'transparent',
                                        color: theme.colors.text,
                                        '&:hover': {
                                            borderColor: theme.colors.border || '#ccc',
                                            backgroundColor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)',
                                        },
                                    }}
                                >
                                    Cancel
                                </Button>
                            ) : (
                                <Button
                                    type="submit"
                                    variant="outlined"
                                    fullWidth
                                    sx={{
                                        border: `1px solid ${theme.colors.secondary || theme.colors.primary || theme.colors.brandSecondary}`,
                                        backgroundColor: 'transparent',
                                        color: theme.colors.secondary || theme.colors.primary || theme.colors.brandSecondary,
                                        '&:hover': {
                                            borderColor: theme.colors.secondary || theme.colors.primary || theme.colors.brandSecondary,
                                            backgroundColor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)',
                                        },
                                    }}
                                >
                                    Add
                                </Button>
                            )}
                        </Box>
                    </TableCell>
                    <TableCell sx={{ 
                        width: '3.5rem !important', 
                        minWidth: '3.5rem !important', 
                        maxWidth: '3.5rem !important', 
                        p: 0,
                        flexShrink: 0,
                        flexGrow: 0,
                    }}></TableCell>
                </TableRow>
            )}

            {/* Add Button Row */}
            {!isAddingUser && !editingUserId && isOwner && (
                <TableRow>
                    <TableCell sx={{ width: '3.5rem', minWidth: '3.5rem', maxWidth: '3.5rem', p: 0 }}></TableCell>
                    <TableCell colSpan={2} sx={{ pl: 0, pr: 0, py: 1 }}>
                        <Button
                            variant="outlined"
                            onClick={handleStartAdd}
                            startIcon={<AddIcon />}
                            fullWidth
                            sx={{
                                borderColor: theme.current === 'light'
                                    ? theme.colors.brandSecondary
                                    : (theme.colors.secondary || theme.colors.brandSecondary),
                                color: theme.current === 'light' 
                                    ? '#000000' 
                                    : (theme.colors.secondary || theme.colors.brandSecondary),
                                backgroundColor: theme.current === 'light'
                                    ? theme.colors.brandSecondary
                                    : (theme.colors.tableBackground || theme.colors.background),
                                '&:hover': {
                                    borderColor: theme.current === 'light'
                                        ? theme.colors.brandSecondary
                                        : (theme.colors.secondary || theme.colors.brandSecondary),
                                    backgroundColor: theme.current === 'light'
                                        ? theme.colors.brandSecondary
                                        : (theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)'),
                                },
                            }}
                        >
                            Add user
                        </Button>
                    </TableCell>
                    <TableCell sx={{ width: '3.5rem', minWidth: '3.5rem', maxWidth: '3.5rem', p: 0 }}></TableCell>
                </TableRow>
            )}
        </>
    );
};

export default AddParticipantRow;
